import type { MonthSeries, RangeMetrics } from '../types/dashboard';
import { fmtCompact, fmtCurrency, fmtCurrencyCompact, fmtInt, fmtPct, fmtPP, fmtRatio, fmtSignedPct, pctChange } from './format';

export type DashMetric = 'members' | 'registrations' | 'conversion' | 'revenue' | 'regsPerMember' | 'retention';
export type TrendMetric = Exclude<DashMetric, 'retention'>;

export interface MetricMeta {
  id: DashMetric;
  label: string;
  definitionId: string;
}

export const METRICS: MetricMeta[] = [
  { id: 'members', label: 'Members', definitionId: 'members' },
  { id: 'registrations', label: 'Registrations', definitionId: 'registrations' },
  { id: 'conversion', label: 'Conversion Rate', definitionId: 'conversion' },
  { id: 'revenue', label: 'Revenue', definitionId: 'revenue' },
  { id: 'regsPerMember', label: 'Registrations per Member', definitionId: 'regsPerMember' },
  { id: 'retention', label: 'Retention', definitionId: 'seasonRetention' },
];

export const metricMeta = (id: DashMetric): MetricMeta => METRICS.find((m) => m.id === id) ?? METRICS[0];
export const isCountMetric = (m: TrendMetric) => m === 'members' || m === 'registrations' || m === 'revenue';
export const changeKind = (m: TrendMetric): 'pct' | 'pp' => (m === 'conversion' ? 'pp' : 'pct');

export function rangeValue(m: TrendMetric, r: RangeMetrics): number {
  switch (m) {
    case 'members': return r.members;
    case 'registrations': return r.registrations;
    case 'revenue': return r.revenue;
    case 'conversion': return r.conversionRate;
    case 'regsPerMember': return r.regsPerMember;
  }
}

/** Monthly values use same-month activity: members added that month who registered that month. */
export function monthValue(m: TrendMetric, s: MonthSeries, i: number): number | null {
  if (i < 0 || i >= s.members.length) return null;
  switch (m) {
    case 'members': return s.members[i];
    case 'registrations': return s.regs[0][i];
    case 'revenue': return s.rev[0][i];
    case 'conversion': return s.members[i] ? s.conv[0][i] / s.members[i] : null;
    case 'regsPerMember': return s.conv[0][i] ? s.regs[0][i] / s.conv[0][i] : null;
  }
}

/** % change for counts and ratios, percentage-point difference for conversion. */
export function change(m: TrendMetric, cur: number | null, prev: number | null | undefined): number | null {
  if (cur === null || prev === null || prev === undefined) return null;
  return changeKind(m) === 'pp' ? cur - prev : pctChange(cur, prev);
}

export function formatValue(m: TrendMetric, v: number): string {
  if (m === 'revenue') return fmtCurrency(v);
  if (m === 'conversion') return fmtPct(v);
  if (m === 'regsPerMember') return fmtRatio(v);
  return fmtInt(v);
}

export function formatAxis(m: TrendMetric, v: number): string {
  if (m === 'revenue') return fmtCurrencyCompact(v);
  if (m === 'conversion') return fmtPct(v, 0);
  if (m === 'regsPerMember') return fmtRatio(v, 1);
  return fmtCompact(v);
}

export function formatChange(m: TrendMetric, v: number): string {
  return changeKind(m) === 'pp' ? fmtPP(v) : fmtSignedPct(v);
}
